import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  getBankTransactionSummary,
  getEbBankTransactions,
  matchEbInvoices,
  setEbTransactionVisibility,
  setTransactionsFilterPeriodApi,
  unMatchEbInvoices,
} from 'client/api/banking'
import { Analytics } from 'client/Global/Analytics'
import { trackingFields } from 'client/Global/AnalyticsEvents'
import {
  BankTransactionRequest,
  BankTransactionsState,
  MatchInvoiceRequest,
  TransactionsFilterPeriodRequest,
  TransactionViewOptionRequest,
  UnMatchInvoiceRequest,
} from 'client/Interfaces/Banking'

import { PeriodPickerValue } from '../common/PeriodPicker/PeriodPicker'
import { BankingProviders } from './BankingModals/ConnectBankModal'

const bankTransactionsInitialState: BankTransactionsState = {
  bankTransactions: [],
  selectedDatePeriod: null,
  currentMatchStatus: null,
  status: 'idle',
  error: null,
  bankTransactionSummary: null,
}

export const fetchBankTransactions = createAsyncThunk(
  'bankTransactions/fetchBankTransactions',
  async (bankTransactionReqBody: BankTransactionRequest) => {
    if (bankTransactionReqBody.selectedBankingProvider === BankingProviders.eb) {
      const res = await getEbBankTransactions(bankTransactionReqBody.datePeriod)
      Analytics.trackEvent(trackingFields.name.loadTransactions, {
        type: trackingFields.type.backend,
        category: trackingFields.category.bankConnection,
        result: trackingFields.Modifiers.success,
        placement: trackingFields.placement.bankConnection,
        transactionsLoaded: res.transactions.length,
      })
      return { res, ...bankTransactionReqBody.datePeriod }
    }
  },
)

export const fetchBankTransactionSummary = createAsyncThunk(
  'bankTransactions/fetchBankTransactionSummary',
  async () => {
    const res = await getBankTransactionSummary()
    return res
  },
)

export const matchInvoicesWithTransaction = createAsyncThunk(
  'bankTransactions/matchInvoicesWithTransaction',
  async (request: MatchInvoiceRequest) => {
    if (request.selectedBankingProvider === BankingProviders.eb) {
      const response = await matchEbInvoices(request.matchInvoiceReqBody)
      return response.status
    }
  },
)

export const unMatchInvoicesWithTransaction = createAsyncThunk(
  'bankTransactions/unMatchInvoicesWithTransaction',
  async (request: UnMatchInvoiceRequest) => {
    if (request.selectedBankingProvider === BankingProviders.eb) {
      const response = await unMatchEbInvoices(request.unMatchInvoiceReqBody)
      return response.status
    }
  },
)

export const setTransactionViewOption = createAsyncThunk(
  'bankTransactions/setTransactionVisibility',
  async (request: TransactionViewOptionRequest) => {
    if (request.selectedBankingProvider === BankingProviders.eb) {
      const response = await setEbTransactionVisibility(request)
      return response.status
    }
  },
)

export const setTransactionsFilterPeriod = createAsyncThunk(
  'bankTransactions/setTransactionsFilterPeriod',
  async (request: TransactionsFilterPeriodRequest) => {
    const response = await setTransactionsFilterPeriodApi(request)
    return response.bankSettings
  },
)

const bankTransactions = createSlice({
  name: 'bankTransactions',
  initialState: bankTransactionsInitialState,
  reducers: {
    setSelectedDatePeriod(state, action: PayloadAction<PeriodPickerValue>) {
      state.selectedDatePeriod = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // pending
      .addCase(fetchBankTransactions.pending, (state, action) => {
        state.status = 'loading'
      })
      .addCase(fetchBankTransactionSummary.pending, (state, action) => {
        state.status = 'loading'
      })
      .addCase(matchInvoicesWithTransaction.pending, (state, action) => {
        state.status = 'loading'
      })
      .addCase(unMatchInvoicesWithTransaction.pending, (state, action) => {
        state.status = 'loading'
      })
      .addCase(setTransactionViewOption.pending, (state, action) => {
        state.status = 'loading'
      })
      .addCase(setTransactionsFilterPeriod.pending, (state, action) => {
        state.status = 'loading'
      })

      // succeed
      .addCase(fetchBankTransactions.fulfilled, (state, action) => {
        state.bankTransactions = action.payload.res.transactions
        state.settings = action.payload.res.settings
        state.selectedDatePeriod = {
          start: action.payload.res.dateStart,
          end: action.payload.res.dateEnd,
          id: action.payload.id,
        }
        state.status = 'succeed'
      })
      .addCase(fetchBankTransactionSummary.fulfilled, (state, action) => {
        state.bankTransactionSummary = action.payload
        state.status = 'succeed'
      })
      .addCase(matchInvoicesWithTransaction.fulfilled, (state, action) => {
        state.currentMatchStatus = action.payload
        state.status = 'succeed'
      })
      .addCase(unMatchInvoicesWithTransaction.fulfilled, (state, action) => {
        state.currentMatchStatus = action.payload
        state.status = 'succeed'
      })
      .addCase(setTransactionViewOption.fulfilled, (state, action) => {
        state.status = 'succeed'
      })
      .addCase(setTransactionsFilterPeriod.fulfilled, (state, action) => {
        state.settings = { ...state.settings, ...action.payload }
        state.status = 'succeed'
      })

      // failed
      .addCase(fetchBankTransactions.rejected, (state, action) => {
        state.bankTransactions = null
        state.error = action.error
        state.status = action.error.message === 'Request failed with status code 403' ? 'expired' : 'failed'
      })
      .addCase(fetchBankTransactionSummary.rejected, (state, action) => {
        state.bankTransactionSummary = null
        state.error = action.error
        state.status = 'failed'
      })
      .addCase(matchInvoicesWithTransaction.rejected, (state, action) => {
        state.error = action.error
        state.status = 'failed'
      })
      .addCase(unMatchInvoicesWithTransaction.rejected, (state, action) => {
        state.error = action.error
        state.status = 'failed'
      })
      .addCase(setTransactionViewOption.rejected, (state, action) => {
        state.error = action.error
        state.status = 'failed'
      })
      .addCase(setTransactionsFilterPeriod.rejected, (state, action) => {
        state.error = action.error
        state.status = 'failed'
      })
  },
})

export const { setSelectedDatePeriod } = bankTransactions.actions
export default bankTransactions.reducer
